import { useEffect } from 'react'
import { Button, DatePicker, Form, Input, Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns'
import {
  FormDebtValue,
  ListRequest,
  OptionType,
} from 'src/pages/finSystem/debt/types'
import { filterOption, normalizeDate } from 'src/pages/finSystem/debt/utils'

dayjs.extend(customParseFormat)

const FIXED_START_DATE = '24.08.2026'

const DATE_FILTER_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'lastWeek', label: 'Last week' },
  { value: 'lastMonth', label: 'Last month' },
  { value: FIXED_START_DATE, label: `From ${FIXED_START_DATE}` },
]

type Props = {
  filters: ListRequest
  setFilters: (values: ListRequest) => void
  resetFilters: () => void
  initialValueForm: FormDebtValue
  optionsWalletWithArchive: OptionType[]
}

export const DebtListFilter = ({
  filters,
  setFilters,
  resetFilters,
  optionsWalletWithArchive,
  initialValueForm,
}: Props) => {
  const [form] = Form.useForm()
  const { Item } = Form
  const { RangePicker } = DatePicker

  const dateFilter = filters.dateFilter ?? 'date'

  useEffect(() => {
    if (dateFilter === 'date') return

    const today = new Date()

    if (dateFilter === 'today') {
      const value = normalizeDate(today)
      setFilters({ dateFrom: value, dateTo: value })
      return
    }

    if (dateFilter === 'yesterday') {
      const value = normalizeDate(subDays(today, 1))
      setFilters({ dateFrom: value, dateTo: value })
      return
    }

    if (dateFilter === 'lastWeek') {
      setFilters({
        dateFrom: normalizeDate(
          startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
        ),
        dateTo: normalizeDate(
          endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
        ),
      })
      return
    }

    if (dateFilter === 'lastMonth') {
      setFilters({
        dateFrom: normalizeDate(startOfMonth(subMonths(today, 1))),
        dateTo: normalizeDate(endOfMonth(subMonths(today, 1))),
      })
      return
    }

    if (dayjs(dateFilter, 'DD.MM.YYYY', true).isValid()) {
      setFilters({
        dateFrom: normalizeDate(dayjs(dateFilter, 'DD.MM.YYYY').toDate()),
        dateTo: normalizeDate(today),
      })
    }
  }, [dateFilter])

  const handleChangeForm = (valueField: Record<string, any>) => {
    setFilters({ page: '1' })
    const [key, value] = Object.entries(valueField)[0]
    if (key === 'userId' && value.length < 3 && value.length > 0) return

    if (key === 'dateFilter') {
      setFilters({
        dateFilter: value,
        ...(value !== 'date' ? { dateFrom: undefined, dateTo: undefined } : {}),
      })
      return
    }

    if (key === 'period') {
      if (!value) {
        setFilters({ dateFrom: undefined, dateTo: undefined })
        return
      }
      const [dateFromPicker, dateToPicker] = value ?? []
      const dateFrom = normalizeDate(dateFromPicker)
      const dateTo = normalizeDate(dateToPicker)
      setFilters({ dateFrom, dateTo })
      return
    }

    setFilters({ [key]: value })
  }

  const visibleReset = () => {
    const keys = Object.keys(filters)

    if (
      keys.some((key) =>
        [
          'id',
          'walletIds',
          'userId',
          'statuses',
          'dateFrom',
          'dateTo',
        ].includes(key),
      )
    ) {
      return true
    }
    return false
  }

  return (
    <Form
      form={form}
      layout="inline"
      onValuesChange={handleChangeForm}
      initialValues={initialValueForm}
    >
      <div style={{ height: 55 }}></div>
      <Item name="id" key="id" style={{ width: 160 }}>
        <Input
          placeholder="Search ID CRM"
          allowClear
          prefix={
            <SearchOutlined
              style={{ color: 'var(--color-gray-60)', fontSize: 12 }}
            />
          }
        />
      </Item>

      <Item name="walletIds" key="walletIds" style={{ width: 250 }}>
        <Select
          placeholder="Wallets"
          mode="multiple"
          allowClear
          options={optionsWalletWithArchive}
          filterOption={filterOption}
        />
      </Item>

      <Item
        name="userId"
        key="userId"
        style={{ width: 160 }}
        rules={[
          {
            min: 3,
            message: 'Minimum 3 chars',
          },
        ]}
      >
        <Input
          placeholder="Search User ID"
          allowClear
          prefix={
            <SearchOutlined
              style={{ color: 'var(--color-gray-60)', fontSize: 12 }}
            />
          }
        />
      </Item>

      <Item name="statuses" key="statuses" style={{ width: 130 }}>
        <Select
          placeholder="Status"
          mode="multiple"
          allowClear
          options={[
            { value: 'Active', label: 'Active' },
            { value: 'Closed', label: 'Closed' },
          ]}
          filterOption={filterOption}
        />
      </Item>

      <div style={{ flexBasis: '100%', display: 'flex', marginBottom: 16 }}>
        <Item name="dateFilter" key="dateFilter" style={{ width: 180 }}>
          <Select options={DATE_FILTER_OPTIONS} />
        </Item>

        {dateFilter === 'date' && (
          <Item name="period" key="period" style={{ width: 250 }}>
            <RangePicker />
          </Item>
        )}

        {visibleReset() && (
          <Item>
            <Button
              onClick={() => {
                form.setFieldsValue({
                  id: undefined,
                  walletIds: undefined,
                  userId: undefined,
                  statuses: undefined,
                  dateFilter: 'date',
                  period: undefined,
                })
                resetFilters()
              }}
            >
              Reset filters
            </Button>
          </Item>
        )}
      </div>
    </Form>
  )
}
